import React from 'react';
import { useSelector } from 'react-redux';
import { Col, Form, Row } from 'antd';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import { defaultBank } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import type { PayrollFinanceSectionProps } from './types';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';

const PaymentDetailsSection: React.FC<PayrollFinanceSectionProps> = ({
  visibility,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  // NX-5228 made Account Name + IBAN required for Ireland here, but NX-6211
  // reverses that for agencies: an agency is billed, not paid by SEPA, so its
  // bank details are optional and are often not known when the agency record is
  // first created. The Irish IBAN requirement stays where money actually leaves
  // the account (employee/company bank details). Format rules below still apply
  // when a value is entered; the building society number is UK-only.
  const isIreland = payrollCountry === IRELAND;

  // const handleVerifyBank = async (params: any) => {
  //   try {
  //     setVerifyBankLoading(true);
  //     verifyBankAccount({
  //       params,
  //       cb: (data: any) => {
  //         if (!data) return;
  //         if (!data?.Error) {
  //           showMessage({
  //             type: 'success',
  //             content: messages['common.verifyBankSuccess'],
  //           });
  //         } else {
  //           const { Cause, Description, Resolution } = data;
  //           showMessage({
  //             type: 'error',
  //             content: `${Cause} ${Description}. ${Resolution}`,
  //           });
  //         }
  //         setVerifyBankLoading(false);
  //       },
  //     });
  //   } catch (err) {
  //     console.error(err);
  //   }
  // };

  if (!visibility.paymentDetails) return null;

  return (
    <div className="pt-6">
      <div className="mb-6 text-xl font-medium text-gray-800">
        {messages['common.paymentDetails']}
      </div>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={messages['common.bankName']}
            name={['paymentDetails', 'bankName']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppSelect
              options={defaultBank}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={messages['common.accountName']}
            name={['paymentDetails', 'accountName']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={messages['common.accountNumber8Digits']}
            name={['paymentDetails', 'accountNumber']}
            rules={[
              {
                pattern: /^\d{8}$/,
                message: messages['form.accountNumberMustBe8Digits'],
              },
            ]}
            normalize={(value: string) => value.replace(/[^0-9]/g, '')}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            className="[&_.ant-form-item-explain]:whitespace-nowrap"
          >
            <AppInput placeholder={messages['form.enter']} maxLength={8} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={messages['common.sortCode6Digits']}
            name={['paymentDetails', 'sortCode']}
            rules={[
              {
                pattern: /^\d{6}$/,
                message: 'Sort code must be 6 digits',
              },
            ]}
            normalize={(value: string) => value.replace(/[^0-9]/g, '')}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppInput placeholder={messages['form.enter']} maxLength={6} />
          </Form.Item>
        </Col>
      </Row>
      {!isIreland && (
        <Form.Item
          label={messages['common.buildingSocietyNumberUpto24Digits']}
          name={['paymentDetails', 'buildingSocietyNumber']}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <AppInput placeholder={messages['form.enter']} maxLength={24} />
        </Form.Item>
      )}
      <Form.Item
        label={messages['common.IBANReferenceUpto30Digits']}
        name={['paymentDetails', 'IBANReference']}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <AppInput placeholder={messages['form.enter']} maxLength={30} />
      </Form.Item>
      <Form.Item
        label={messages['common.SWIFTNoUpto20Digits']}
        name={['paymentDetails', 'SWIFTNo']}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <AppInput placeholder={messages['form.enter']} maxLength={20} />
      </Form.Item>
    </div>
  );
};

export default PaymentDetailsSection;
